/**
 * What this visitor's region allows and how the consent boxes start, as the
 * server decides it from the edge-reported country (`/api/region/availability`).
 *
 * The client never holds the country table: it renders what the server says,
 * and the server re-derives the same rule when it records a decision. One
 * fetch per page, shared by the consent gate and the store.
 *
 * A failed or malformed answer yields the STRICT presentation — every box
 * empty — because failing open would pre-tick a box for a visitor whose law
 * forbids it. Presenting the strict form to someone whose law would have
 * allowed a pre-tick costs a click; the other direction costs the consent.
 */

const STRICT_DEFAULTS = Object.freeze({
  country: null,
  tier: "strict",
  analytics: "unticked",
  email: "unticked",
  purchaseEmail: "unticked",
});

/* A hung request must not hold the consent gate open forever: past this the
   strict presentation is used, exactly as for a failed request. */
const FETCH_TIMEOUT_MS = 4000;

const PRESENTATIONS = new Set(["preticked", "unticked"]);

/** Nothing added, which is also what every malformed answer means. */
const NO_TAX = Object.freeze({ collected: false, taxType: null, percentage: null, amounts: null });

/**
 * What this visitor's country adds to the prices the Store prints, as Stripe
 * Tax answered it server-side.
 *
 * VALIDATED HERE, ONCE. A total below its own net amount, a non-integer, a
 * missing rate — any of them would put a figure on a price tag that no
 * checkout will charge, so the whole answer is discarded and the Store keeps
 * the tax-exclusive presentation it has always had.
 */
function normalizeTax(raw) {
  if (!raw || typeof raw !== "object" || raw.collected !== true) return NO_TAX;
  const percentage = String(raw.percentage || "").trim();
  if (!/^\d+(\.\d+)?$/.test(percentage) || Number(percentage) <= 0) return NO_TAX;
  const source = raw.amounts;
  if (!source || typeof source !== "object") return NO_TAX;
  const amounts = {};
  for (const [key, value] of Object.entries(source)) {
    const amountMinor = Number(key);
    const totalMinor = Number(value);
    if (!Number.isSafeInteger(amountMinor) || amountMinor <= 0) return NO_TAX;
    if (!Number.isSafeInteger(totalMinor) || totalMinor < amountMinor) return NO_TAX;
    amounts[String(amountMinor)] = totalMinor;
  }
  if (Object.keys(amounts).length === 0) return NO_TAX;
  return {
    collected: true,
    taxType: typeof raw.taxType === "string" ? raw.taxType : null,
    percentage,
    amounts,
  };
}

let inflight = null;

function normalizeDefaults(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const pick = (key) => (PRESENTATIONS.has(source[key]) ? source[key] : STRICT_DEFAULTS[key]);
  return {
    country: typeof source.country === "string" ? source.country : null,
    tier: typeof source.tier === "string" ? source.tier : STRICT_DEFAULTS.tier,
    analytics: pick("analytics"),
    email: pick("email"),
    purchaseEmail: pick("purchaseEmail"),
  };
}

async function fetchAvailability() {
  /** @type {RequestInit} */
  const init = { credentials: "same-origin" };
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    init.signal = AbortSignal.timeout(FETCH_TIMEOUT_MS);
  }
  const res = await fetch("/api/region/availability", init);
  if (!res.ok) throw new Error("region availability " + res.status);
  return res.json();
}

/** @returns {Promise<{country: string|null, serviceBlocked: boolean, purchaseBlocked: boolean, packsBlocked: boolean, consentDefaults: object, tax: object}>} */
function getRegionAvailability() {
  if (!inflight) {
    inflight = fetchAvailability()
      .then((body) => ({
        country: typeof body?.country === "string" ? body.country : null,
        serviceBlocked: body?.serviceBlocked === true,
        purchaseBlocked: body?.purchaseBlocked === true,
        packsBlocked: body?.packsBlocked === true,
        consentDefaults: normalizeDefaults(body?.consentDefaults),
        tax: normalizeTax(body?.tax),
      }))
      .catch(() => ({
        country: null,
        serviceBlocked: false,
        purchaseBlocked: false,
        packsBlocked: false,
        consentDefaults: { ...STRICT_DEFAULTS },
        tax: NO_TAX,
      }));
  }
  return inflight;
}

/** The consent presentation alone; never rejects. */
async function getConsentDefaults() {
  return (await getRegionAvailability()).consentDefaults;
}

/** Test seam: forget the cached answer so the next call asks again. */
function resetRegionAvailabilityCache() {
  inflight = null;
}

const RegionAvailability = {
  NO_TAX,
  STRICT_DEFAULTS,
  getRegionAvailability,
  getConsentDefaults,
  resetRegionAvailabilityCache,
};

export { RegionAvailability };
